import React, { useState } from 'react';
import { BanknoteIcon, GiftIcon, InfoIcon, LockIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { EmptyState } from '../../components/ui/EmptyState';
import { DataTable } from '../../components/ui/DataTable';
import { RedemptionDesk } from '../../components/campagift/RedemptionDesk';
import { useCampaGift } from '../../contexts/CampaGiftContext';
import { DEMO_DATA_NOTICE } from '../../data/campagift';
import { bpsLabel, koboToNaira } from '../../utils/campagift';
import { useSectionRoute } from '../../hooks/useSectionRoute';
const TABS = ['Redemption desk', 'Redemptions', 'Receivables', 'Disputes', 'Configuration'] as const;
const SETTLEMENT_TONE: Record<string, 'ok' | 'warn' | 'brand' | 'neutral' | 'danger'> = {
  settled: 'ok',
  eligible: 'brand',
  processing: 'brand',
  submitted: 'brand',
  pending: 'neutral',
  failed: 'danger',
  held: 'danger',
  disputed: 'danger',
  reversed: 'warn'
};
export function MerchantConsole() {
  const {
    merchantId,
    merchantOf,
    redemptions,
    settlements,
    disputes,
    gifts,
    types,
    flags,
    openSettlement,
    advanceSettlement
  } = useCampaGift();
  const [tab, setTab] = useSectionRoute('/merchant', TABS);
  const merchant = merchantOf(merchantId);
  if (!merchant) return null;
  const mine = redemptions.filter(r => r.merchantId === merchantId);
  /* Reversed originals and their compensating entries both count, so the pair
     nets to zero rather than being refunded twice. */
  const confirmed = mine;
  const grossKobo = confirmed.reduce((total, r) => total + r.amountKobo, 0);
  const commissionKobo = confirmed.reduce((total, r) => total + r.commissionKobo, 0);
  const unsettled = confirmed.filter(r => !r.settlementId);
  const receivableKobo = unsettled.reduce((total, r) => total + r.netReceivableKobo, 0);
  const mySettlements = settlements.filter(s => s.merchantId === merchantId);
  const settledKobo = mySettlements.filter(s => s.state === 'settled').reduce((total, s) => total + s.netKobo, 0);
  const myDisputes = disputes.filter(d => mine.some(r => r.giftId === d.giftId));
  const settlementEnabled = flags.find(f => f.key === 'campa_gift_settlement')?.enabled ?? false;
  return <div className="space-y-6" data-id="element-9292" data-mp="ut40ayr958pw">
      <PageHeader eyebrow={`${merchant.name} · ${merchant.campus}`} title="CampaGift" description="Accept CampaGift at your till, track what Campuzon owes you, and reconcile every settlement against the redemptions behind it." actions={<Badge tone={merchant.participation === 'active' ? 'ok' : 'warn'} data-id="element-9294" data-mp="1dhsfr56yulua">
            {merchant.participation === 'active' ? 'Accepting CampaGift' : 'Participation pending'}
          </Badge>} data-id="element-9293" data-mp="pry24letot8z" />

      <p className="rounded-card border border-line bg-canvas px-4 py-2.5 text-caption text-ink-muted" data-id="element-9295" data-mp="1g2gncukknsss">
        {DEMO_DATA_NOTICE}
      </p>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-9296" data-mp="1l1tl4aczeka5">
        <div className="rounded-card border border-brand-700 bg-surface px-5 py-4" data-id="element-9297" data-mp="1pzv30ryo8dk1">
          <p className="text-body-sm text-ink-muted" data-id="element-9298" data-mp="1uz7q9dtgfmn5">Current receivable</p>
          <p className="cz-num mt-1.5 text-h1 font-semibold text-ink" data-id="element-9299" data-mp="1uxej7xexj0em">
            {koboToNaira(receivableKobo)}
          </p>
          <p className="mt-1 text-caption text-ink-muted" data-id="element-9300" data-mp="23zw52aqo9r13">
            {unsettled.length} redemption{unsettled.length === 1 ? '' : 's'} not yet in a settlement
          </p>
        </div>
        <div className="rounded-card border border-line bg-surface px-5 py-4" data-id="element-9301" data-mp="2dpz4otsau38e">
          <p className="text-body-sm text-ink-muted" data-id="element-9302" data-mp="2ds1rz622hi9f">Gross redeemed</p>
          <p className="cz-num mt-1.5 text-h2 font-semibold text-ink" data-id="element-9303" data-mp="2iqd79wb4skz1">{koboToNaira(grossKobo)}</p>
          <p className="mt-1 text-caption text-ink-muted" data-id="element-9304" data-mp="2mrgksc5pi4x3">{confirmed.length} redemptions</p>
        </div>
        <div className="rounded-card border border-line bg-surface px-5 py-4" data-id="element-9305" data-mp="3kdywgpdp84u0">
          <p className="text-body-sm text-ink-muted" data-id="element-9306" data-mp="3ffdxsue3ht7o">Commission</p>
          <p className="cz-num mt-1.5 text-h2 font-semibold text-ink" data-id="element-9307" data-mp="3t7lr2c66721m">
            {koboToNaira(commissionKobo)}
          </p>
          <p className="mt-1 text-caption text-ink-muted" data-id="element-9308" data-mp="3p7b90153sl6e">
            Your rate: {bpsLabel(merchant.commissionBps ?? 0)}
          </p>
        </div>
        <div className="rounded-card border border-line bg-surface px-5 py-4" data-id="element-9309" data-mp="31iilmt2jrlne">
          <p className="text-body-sm text-ink-muted" data-id="element-9310" data-mp="2wjfen330b5cs">Settled to date</p>
          <p className="cz-num mt-1.5 text-h2 font-semibold text-ink" data-id="element-9311" data-mp="3aif247es9e7d">{koboToNaira(settledKobo)}</p>
          <p className="mt-1 text-caption text-ink-muted" data-id="element-9312" data-mp="35h7uc8cn4f0p">{merchant.settlementSchedule}</p>
        </div>
      </div>

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Merchant sections" data-id="element-9313" data-mp="ppjzcih6swkj" />

      {tab === 'Redemption desk' && <RedemptionDesk data-id="element-9314" data-mp="krsbq0nkrwm5" />}

      {tab === 'Redemptions' && <Panel title="Redemption history" bodyClassName="p-0" data-id="element-9315" data-mp="t8st4nquye0y">
          {mine.length === 0 ? <div className="px-5 py-4" data-id="element-9316" data-mp="xaw7bk4wvwpx">
              <EmptyState icon={<GiftIcon aria-hidden className="h-6 w-6" data-id="element-9318" data-mp="jb4pt9f6ry76" />} title="No redemption history yet" description="Accepted CampaGifts appear here with commission and settlement reference." data-id="element-9317" data-mp="x8jhylwtqk2u" />
            </div> : <DataTable caption="Redemptions accepted at this merchant, with commission and settlement reference" rowKey={item => item.id} rows={mine} columns={[{
        header: 'Reference',
        primary: true,
        cell: item => <span className="cz-num" data-id="element-9320" data-mp="1uy4ox41sklmn">
                      {item.reference}
                      <span className="mt-0.5 block text-caption font-normal text-ink-muted" data-id="element-9321" data-mp="1zouxsr9ff6m3">
                        {item.at.slice(0, 10)}
                      </span>
                    </span>
      }, {
        header: 'Gift',
        cell: item => <span className="cz-num text-ink-muted" data-id="element-9322" data-mp="1zlw3se1qnpfb">
                      {gifts.find(g => g.id === item.giftId)?.publicReference ?? '—'}
                      <span className="mt-0.5 block text-caption text-ink-subtle" data-id="element-9323" data-mp="1vnoj5845m3yu">
                        Customer identity withheld
                      </span>
                    </span>
      }, {
        header: 'Gross',
        align: 'right',
        cell: item => <span className="cz-num" data-id="element-9324" data-mp="1bxuwt9k7kh20">{koboToNaira(item.amountKobo)}</span>
      }, {
        header: 'Commission',
        align: 'right',
        cell: item => <span className="cz-num text-ink-muted" data-id="element-9325" data-mp="12xzhcsjmdzw6">
                      {koboToNaira(item.commissionKobo)}
                    </span>
      }, {
        header: 'Net',
        align: 'right',
        cell: item => <span className="cz-num font-semibold" data-id="element-9326" data-mp="3nl7vbg4042ls">
                      {koboToNaira(item.netReceivableKobo)}
                    </span>
      }, {
        header: 'Settlement',
        cell: item => <span className="cz-num text-ink-muted" data-id="element-9327" data-mp="2zw5h6i8ksrw1">
                      {settlements.find(s => s.id === item.settlementId)?.reference ?? 'Not yet settled'}
                    </span>
      }, {
        header: 'State',
        status: true,
        cell: item => <Badge tone={item.state === 'confirmed' ? 'ok' : 'warn'} plain={item.state === 'confirmed'} data-id="element-9328" data-mp="2h44e13orqfgt">
                      {item.reversalOfReference ? 'Reversal entry' : item.state === 'reversed' ? 'Reversed' : 'Confirmed'}
                    </Badge>
      }]} data-id="element-9319" data-mp="1c0buyr95bm1e" />}
          <p className="border-t border-line px-4 py-3 text-caption text-ink-muted" data-id="element-9329" data-mp="1tfe8s8oi6rqt">
            You see the gift reference, never the customer's name, handle or other gifts. A reversal
            appears as its own row beside the original, which is left intact.
          </p>
        </Panel>}

      {tab === 'Receivables' && <div className="space-y-6" data-id="element-9330" data-mp="1y7o5c7k4iq22">
          <Panel title="Settlements" description="Every settlement lists the redemptions behind it, so the amount can be reconciled line by line." action={<Button size="sm" variant="secondary" onClick={() => openSettlement(merchantId, `${merchant.name} · admin`)} disabled={unsettled.length === 0} data-id="element-9332" data-mp="2qtcqr9ai740x">
                Open settlement for {unsettled.length} redemption
                {unsettled.length === 1 ? '' : 's'}
              </Button>} bodyClassName="p-0" data-id="element-9331" data-mp="2c6nkmolqxslk">
            <ul className="divide-y divide-line" data-id="element-9333" data-mp="281tq393yc222">
              {mySettlements.map(settlement => <li key={settlement.id} className="px-5 py-4" data-id="element-9334" data-mp="2cumsvrfv10bi">
                  <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-9335" data-mp="1y4ysqtjf07pd">
                    <div className="min-w-0" data-id="element-9336" data-mp="233rx9wgq4ode">
                      <p className="cz-num text-body font-semibold text-ink" data-id="element-9337" data-mp="234ju1am24s59">
                        {settlement.reference}
                      </p>
                      <p className="mt-0.5 text-caption text-ink-muted" data-id="element-9338" data-mp="1u33q9lpqzoa0">
                        Opened {settlement.openedAt}
                        {settlement.settledAt ? ` · settled ${settlement.settledAt}` : ''} ·{' '}
                        {settlement.provider}
                      </p>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-9339" data-mp="3wha54kb0f9p">
                      <Badge tone={SETTLEMENT_TONE[settlement.state] ?? 'neutral'} data-id="element-9340" data-mp="3yae2c3j8le0">
                        {settlement.state.charAt(0).toUpperCase() + settlement.state.slice(1)}
                      </Badge>
                      {settlement.state !== 'settled' && <Button size="sm" variant="secondary" onClick={() => advanceSettlement(settlement.id, 'Finance · adaeze.k')} data-id="element-9341" data-mp="10lovabh0gmd3">
                          Advance state
                        </Button>}
                    </div>
                  </div>
                  <dl className="mt-3 grid gap-x-6 gap-y-1.5 text-body-sm sm:grid-cols-4" data-id="element-9342" data-mp="1kb8y95af30d7">
                    <div data-id="element-9343" data-mp="1to7up449r87b">
                      <dt className="text-ink-muted" data-id="element-9344" data-mp="1tng7agktsgs4">Gross</dt>
                      <dd className="cz-num text-ink" data-id="element-9345" data-mp="1po7djbpsciwk">{koboToNaira(settlement.grossKobo)}</dd>
                    </div>
                    <div data-id="element-9346" data-mp="23dhjob3tgar7">
                      <dt className="text-ink-muted" data-id="element-9347" data-mp="23e15w40i1lb1">Commission</dt>
                      <dd className="cz-num text-ink" data-id="element-9348" data-mp="1ygr7hyv4tzb2">−{koboToNaira(settlement.commissionKobo).replace('₦', '₦')}</dd>
                    </div>
                    <div data-id="element-9349" data-mp="1aryg8fpggfms">
                      <dt className="text-ink-muted" data-id="element-9350" data-mp="16p1syboyzvci">Adjustments</dt>
                      <dd className="cz-num text-ink" data-id="element-9351" data-mp="16qwffufw3vip">
                        {koboToNaira(settlement.adjustmentsKobo)}
                      </dd>
                    </div>
                    <div data-id="element-9352" data-mp="1fnn9606yyya4">
                      <dt className="text-ink-muted" data-id="element-9353" data-mp="1frkv0tkxm3xp">Net settlement</dt>
                      <dd className="cz-num font-semibold text-ink" data-id="element-9354" data-mp="2w1wxiub7on0y">
                        {koboToNaira(settlement.netKobo)}
                      </dd>
                    </div>
                  </dl>
                  <p className="cz-num mt-2 text-caption text-ink-muted" data-id="element-9355" data-mp="2z4jgu9y8lpkj">
                    Redemptions: {settlement.redemptionReferences.join(', ')}
                    {settlement.providerReference ? ` · provider ref ${settlement.providerReference}` : ''}
                  </p>
                </li>)}
            </ul>
          </Panel>

          {!settlementEnabled && <div className="rounded-card border border-warn-100 bg-warn-50 px-5 py-4" data-id="element-9356" data-mp="2kg6jp9ney0og">
              <div className="flex flex-wrap items-center gap-2" data-id="element-9357" data-mp="2kdu4v5cyc6fm">
                <Badge tone="warn" icon={<LockIcon aria-hidden className="h-4 w-4" data-id="element-9359" data-mp="2pd6q5lo6d5g3" />} data-id="element-9358" data-mp="2pehx1ucx2tbf">
                  Disbursement is disabled
                </Badge>
              </div>
              <p className="mt-2 text-body-sm text-ink-muted" data-id="element-9360" data-mp="1wrdx43sol56i">
                Receivables accrue and reconcile normally, but Campuzon does not move the money
                itself. Paying you out requires a licensed payout provider, and{' '}
                <code className="cz-num" data-id="element-9361" data-mp="1u5ei84mf88c5">campa_gift_settlement</code> stays off until that is in
                place. Your settlement account on file: {merchant.settlementAccountMasked}.
              </p>
            </div>}
        </div>}

      {tab === 'Disputes' && <Panel title="Disputes touching your redemptions" bodyClassName="p-0" data-id="element-9362" data-mp="1f8dpbxshu7c3">
          {myDisputes.length === 0 ? <div className="px-5 py-4" data-id="element-9363" data-mp="1b9emciqbokna">
              <EmptyState icon={<InfoIcon aria-hidden className="h-6 w-6" data-id="element-9365" data-mp="16f0s4jnecjk7" />} title="No open disputes" description="Disputes raised by customers or by you appear here with the decision and reason." data-id="element-9364" data-mp="16gbt6bpgjd7m" />
            </div> : <ul className="divide-y divide-line" data-id="element-9366" data-mp="rjuhtkh9pyl2">
              {myDisputes.map(dispute => <li key={dispute.id} className="px-5 py-4" data-id="element-9367" data-mp="nh7otg8v27ma">
                  <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-9368" data-mp="nju2lxmchjcy">
                    <p className="text-body font-semibold text-ink" data-id="element-9369" data-mp="1c95fpjd6dwgb">{dispute.kind}</p>
                    <Badge tone={dispute.state === 'resolved' ? 'ok' : dispute.state === 'rejected' ? 'danger' : 'warn'} data-id="element-9370" data-mp="1h6yn2fq7swi6">
                      {dispute.state.replace('_', ' ')}
                    </Badge>
                  </div>
                  <p className="cz-num mt-0.5 text-caption text-ink-muted" data-id="element-9371" data-mp="yemaz19reguu">
                    {dispute.reference} · raised by {dispute.raisedBy} · {dispute.at}
                  </p>
                  <p className="mt-2 text-body-sm text-ink-muted" data-id="element-9372" data-mp="oj2sbgk7heo1">{dispute.detail}</p>
                  {dispute.decision && <p className="mt-2 rounded-md border border-line bg-canvas px-3 py-2 text-body-sm text-ink" data-id="element-9373" data-mp="2iwtw3zfut4bk">
                      <span className="font-semibold" data-id="element-9374" data-mp="78yv4eit28bf">Decision · {dispute.reviewer}:</span>{' '}
                      {dispute.decision}
                    </p>}
                </li>)}
            </ul>}
        </Panel>}

      {tab === 'Configuration' && <div className="grid gap-6 lg:grid-cols-2" data-id="element-9375" data-mp="c81p93x8yu4m">
          <Panel title="Your CampaGift configuration" data-id="element-9376" data-mp="c6gv3feuhd7a">
            <dl className="space-y-3 text-body-sm" data-id="element-9377" data-mp="qwofw1sbr8b5">
              <div className="flex justify-between gap-4" data-id="element-9378" data-mp="qzang1f7tntd">
                <dt className="text-ink-muted" data-id="element-9379" data-mp="lxl8wbssas0j">CampaGift</dt>
                <dd className="text-ink" data-id="element-9380" data-mp="zxw05fy0a8m8">
                  {merchant.campaGiftEnabled ? 'Enabled' : 'Disabled'}
                </dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-9381" data-mp="uzkuziinomac">
                <dt className="text-ink-muted" data-id="element-9382" data-mp="31ozg8kqo0fe6">Commission rate</dt>
                <dd className="text-ink" data-id="element-9383" data-mp="31rc39cba5fy3">{bpsLabel(merchant.commissionBps ?? 0)}</dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-9384" data-mp="10n9mn04qt5d2">
                <dt className="text-ink-muted" data-id="element-9385" data-mp="10ougenj8gk9v">Settlement schedule</dt>
                <dd className="text-ink" data-id="element-9386" data-mp="15lkxne6rxlwv">{merchant.settlementSchedule}</dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-9387" data-mp="1al7lehk8akjx">
                <dt className="text-ink-muted" data-id="element-9388" data-mp="1antrsdvduu0k">Daily redemption cap</dt>
                <dd className="cz-num text-ink" data-id="element-9389" data-mp="1eom0n60cewwb">
                  {merchant.dailyRedemptionCapKobo ? koboToNaira(merchant.dailyRedemptionCapKobo) : 'No cap'}
                </dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-9390" data-mp="1tbyox2hjbgqe">
                <dt className="text-ink-muted" data-id="element-9391" data-mp="1tcguipq377az">Settlement account</dt>
                <dd className="cz-num text-ink" data-id="element-9392" data-mp="1ybbcxqk56tuk">{merchant.settlementAccountMasked}</dd>
              </div>
            </dl>
            <p className="mt-4 border-t border-line pt-3 text-caption text-ink-muted" data-id="element-9393" data-mp="22dybeabbjd0q">
              Commission, caps and eligibility are set by Campuzon gift operations. You can see them
              but not change them — a merchant account cannot alter platform-wide gift rules.
            </p>
          </Panel>

          <Panel title="Gift types you accept" bodyClassName="p-0" data-id="element-9394" data-mp="3mtcti5po5y9r">
            <ul className="divide-y divide-line" data-id="element-9395" data-mp="3iu3yyymt1wd5">
              {types.filter(type => merchant.eligibleTypeIds.includes(type.id)).map(type => <li key={type.id} className="px-5 py-3.5" data-id="element-9396" data-mp="ef48biph89rm">
                    <p className="text-body font-medium text-ink" data-id="element-9397" data-mp="egp1b3w57knd">{type.name}</p>
                    <p className="mt-0.5 text-caption text-ink-muted" data-id="element-9398" data-mp="ae2e5gaf2nwi">
                      {type.eligibleCategoryIds === 'all' ? 'Any category' : `Only ${type.eligibleCategoryIds.join(', ')}`}
                      {' · '}
                      {type.partialRedemption ? 'partial allowed' : 'full redemption only'}
                      {' · '}commission {bpsLabel(merchant.commissionBps ?? type.merchantCommissionBps)}
                    </p>
                  </li>)}
            </ul>
            <p className="flex items-start gap-2 border-t border-line px-5 py-3 text-caption text-ink-muted" data-id="element-9399" data-mp="6fsav0m4oj8z">
              <BanknoteIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-9400" data-mp="3nwtgb6x35j7g" />
              Commission is deducted from your receivable, never from the customer's remaining gift
              value.
            </p>
          </Panel>
        </div>}
    </div>;
}